import type { EndClient } from '@lensora/shared'
import { Button, Input, Label } from '@lensora/ui'
import * as React from 'react'

import { ExpiryAndClientFields } from './ExpiryAndClientFields'
import { ScopeChecklist } from './ScopeChecklist'
import type { EXPIRY_OPTIONS } from './scopes'

import type { CreateMcpKeyResult, McpApiKeyPurpose, McpApiKeyScope } from '@/hooks/use-mcp-keys'
import { useCreateLookerKey, useCreateMcpKey } from '@/hooks/use-mcp-keys'
import { ApiError } from '@/lib/api'


// ─── Types ──────────────────────────────────────────────────────────────────

interface CreateKeyModalProps {
  endClients: EndClient[]
  onClose: () => void
  /** Secret only ever flows upward through this callback — never cached. */
  onCreated: (result: CreateMcpKeyResult) => void
  /** Defaults to 'mcp'. In 'looker_studio' mode, scope/restriction/purpose
   * are all server-decided — the form only ever collects name + expiry. */
  purpose?: McpApiKeyPurpose
}

type ExpiryValue = (typeof EXPIRY_OPTIONS)[number]['value']

const COPY = {
  mcp: {
    title: 'Create MCP key',
    description: "Grants a connected client access to this workspace's Lensora data.",
  },
  looker_studio: {
    title: 'Create Looker Studio key',
    description: 'Grants the Looker Studio connector read-only access to this workspace\'s report data.',
  },
} as const

// ─── Component ────────────────────────────────────────────────────────────────

export function CreateKeyModal({ endClients, onClose, onCreated, purpose = 'mcp' }: CreateKeyModalProps) {
  const isLooker = purpose === 'looker_studio'
  const [name, setName] = React.useState('')
  const [scopes, setScopes] = React.useState<McpApiKeyScope[]>(['data:read'])
  const [expiry, setExpiry] = React.useState<ExpiryValue>('90')
  const [restrictedClientId, setRestrictedClientId] = React.useState('')
  const [dailyCreditLimit, setDailyCreditLimit] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const createMcpKey = useCreateMcpKey()
  const createLookerKey = useCreateLookerKey()
  const create = isLooker ? createLookerKey : createMcpKey

  React.useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function toggleScope(scope: McpApiKeyScope) {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    )
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const expiresInDays = expiry === 'never' ? null : Number(expiry)
      const result = isLooker
        ? await createLookerKey.mutateAsync({ name: name.trim(), expiresInDays })
        : await createMcpKey.mutateAsync({
            name: name.trim(),
            scopes,
            expiresInDays,
            restrictedClientId: restrictedClientId || null,
            dailyCreditLimit: dailyCreditLimit.trim() ? Number(dailyCreditLimit) : null,
          })
      onCreated(result)
      // The mutation cache would otherwise hold { key, secret } as `data`
      // until its gcTime elapses (default 5 min), long after this modal is
      // gone. Reset it the moment the secret has been handed up to local
      // state so it doesn't outlive the modal in memory.
      create.reset()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create key')
    }
  }

  const canSubmit = isLooker ? !!name.trim() : scopes.length > 0 && !!name.trim()

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-lg space-y-4 rounded-lg border border-border bg-surface p-6 shadow-lg">
        <div>
          <h2 className="text-lg font-semibold text-text">{COPY[purpose].title}</h2>
          <p className="mt-0.5 text-sm text-text-muted">{COPY[purpose].description}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="mcp-key-name">Name</Label>
            <Input
              id="mcp-key-name"
              required
              placeholder={isLooker ? 'Looker Studio — Monthly report' : 'Claude Desktop — Chris'}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {!isLooker && <ScopeChecklist scopes={scopes} onToggle={toggleScope} />}

          <ExpiryAndClientFields
            expiry={expiry}
            onExpiryChange={setExpiry}
            endClients={endClients}
            restrictedClientId={restrictedClientId}
            onRestrictedClientChange={setRestrictedClientId}
            hideClientPicker={isLooker}
          />

          {isLooker && (
            <p className="rounded-md border border-border bg-surface-raised p-2.5 text-xs text-text-muted">
              This key is automatically scoped to your account and can only read report data —
              there's nothing else to configure.
            </p>
          )}

          {!isLooker && (
            <div className="space-y-2">
              <Label htmlFor="mcp-key-credit-limit">Daily credit limit (optional)</Label>
              <Input
                id="mcp-key-credit-limit"
                type="number"
                min={0}
                max={10000}
                placeholder="No limit"
                value={dailyCreditLimit}
                onChange={(e) => setDailyCreditLimit(e.target.value)}
              />
            </div>
          )}

          {error && <p className="text-sm text-feedback-error">{error}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={create.isPending || !canSubmit}>
              {create.isPending ? 'Creating…' : 'Create key'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
